import type { AttackAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { WarchestState } from "../types/game-state";
import { areAdjacent } from "../utils/hex-utils";
import { getOpponent, hasCoin } from "../utils/zone-utils";

export function validateAttack(
  state: WarchestState,
  action: AttackAction,
): ValidationResult {
  if (state.phase !== "USE_COINS") {
    return { valid: false, error: "Not in USE_COINS phase" };
  }
  if (action.player !== state.activePlayer) {
    return { valid: false, error: "Not your turn" };
  }
  const zones = state.playerZones[action.player];
  if (!hasCoin(zones, action.coinType, "hand")) {
    return { valid: false, error: "Coin not in hand" };
  }
  if (action.coinType === "ROYAL") {
    return { valid: false, error: "Royal Coin cannot attack" };
  }
  const unitDef = state.config.units[action.coinType];
  if (!unitDef?.normalAttackAllowed) {
    return { valid: false, error: "This unit cannot perform a normal attack" };
  }
  const attackerCell = state.board[action.attackerHex];
  if (!attackerCell) {
    return { valid: false, error: "No unit at attacker hex" };
  }
  if (attackerCell.owner !== action.player) {
    return { valid: false, error: "Attacker unit is not yours" };
  }
  if (attackerCell.unitType !== action.coinType) {
    return { valid: false, error: "Coin type does not match attacker unit" };
  }
  const targetCell = state.board[action.targetHex];
  if (!targetCell) {
    return { valid: false, error: "No unit at target hex" };
  }
  if (targetCell.owner !== getOpponent(action.player)) {
    return { valid: false, error: "Target unit is not an enemy" };
  }
  const boardDef = state.config.boards[state.boardId];
  if (
    !boardDef ||
    !areAdjacent(boardDef, action.attackerHex, action.targetHex)
  ) {
    return { valid: false, error: "Target is not adjacent to attacker" };
  }
  const targetUnitDef = state.config.units[targetCell.unitType];
  if (targetUnitDef?.attributeId === "KNIGHT_ARMOR") {
    if (attackerCell.stackSize <= 1) {
      return {
        valid: false,
        error: "Only a bolstered unit can attack the Knight",
      };
    }
  }
  if (action.swordsmanFollowUpHex !== undefined) {
    const attackerUnitDef = state.config.units[action.coinType];
    if (attackerUnitDef?.attributeId === "SWORDSMAN_FOLLOW_THROUGH") {
      if (
        !areAdjacent(boardDef, action.attackerHex, action.swordsmanFollowUpHex)
      ) {
        return {
          valid: false,
          error: "Swordsman follow-up hex is not adjacent to attacker",
        };
      }
      // Allow follow-up into the target hex when the enemy will be destroyed
      const followUpIsTargetHex =
        action.swordsmanFollowUpHex === action.targetHex;
      const targetWillBeDestroyed =
        followUpIsTargetHex && targetCell.stackSize === 1;
      if (
        !targetWillBeDestroyed &&
        state.board[action.swordsmanFollowUpHex] !== null
      ) {
        return { valid: false, error: "Swordsman follow-up hex is not empty" };
      }
    }
  }
  return { valid: true };
}
